import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, Link, useParams, useSearchParams } from "react-router-dom";
import {
  User, Building2, Briefcase, Search, Mail, Lock, ShieldCheck,
  ChevronRight, KeyRound, CheckCircle2,
} from "lucide-react";
import { getTheme, fonts } from "../theme";
import Logo from "../components/Logo";
import Navbar from "../components/Navbar";
import SiteFooter from "../components/SiteFooter";
import { ErrorBanner, PasswordInput } from "../components/UI";
import { useEmailOtp, EmailOtpStatus, otpButtonLabel } from "../components/EmailOtpField";
import api from "../api";

export default function ForgotPassword({ onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("login", themeMode);
  const navigate = useNavigate();
  const { roleParam } = useParams();
  const [searchParams] = useSearchParams();

  const parseRole = (r) => {
    if (!r) return "employee";
    const str = String(r).toLowerCase().trim();
    if (str === "employer" || str === "company") return "employer";
    if (str === "job_seeker" || str === "jobseeker" || str === "job-seeker" || str === "apply_job") return "job_seeker";
    if (str === "recruiter" || str === "hr" || str === "looking_job") return "recruiter";
    return "employee";
  };

  const currentRole = parseRole(roleParam || searchParams.get("role"));
  const [activeTab, setActiveTab] = useState(currentRole);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setActiveTab(currentRole);
    setEmail("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setDone(false);
  }, [currentRole]);

  const roleForBackend = useMemo(() => {
    if (activeTab === "recruiter") return "recruiter";
    if (activeTab === "employer") return "employer";
    if (activeTab === "job_seeker") return "job_seeker";
    return "employee";
  }, [activeTab]);

  const emailOtp = useEmailOtp({
    email,
    purpose: "forgot_password",
    role: roleForBackend,
    onError: setError,
  });

  const switchTab = (newTab) => {
    setActiveTab(newTab);
    setError("");
    setEmail("");
    setNewPassword("");
    setConfirmPassword("");
    setDone(false);
    const urlRole = newTab === "job_seeker" ? "job-seeker" : newTab;
    navigate(`/forgot-password/${urlRole}`, { replace: true });
  };

  const getEmailConfig = () => {
    const placeholder = "you@gmail.com or you@company.com";
    if (activeTab === "employer") return { label: "Employer Email", placeholder };
    if (activeTab === "job_seeker") return { label: "Job Seeker Email", placeholder };
    if (activeTab === "recruiter") return { label: "Recruiter Email", placeholder };
    return { label: "Employee Email", placeholder };
  };

  const loginPath = `/login/${activeTab === "job_seeker" ? "job-seeker" : activeTab}`;
  const emailConfig = getEmailConfig();

  const resetPassword = async (e) => {
    e.preventDefault();
    setError("");
    const trimmedEmail = (email || "").trim().toLowerCase();
    if (!trimmedEmail) {
      setError("Please enter your registered email address.");
      return;
    }
    if (!emailOtp.sent) {
      setError("Please request an OTP first.");
      return;
    }
    if (!/^\d{6}$/.test(String(emailOtp.otp || "").trim())) {
      setError("Enter the 6-digit OTP sent to your email.");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (!/[\d\W]/.test(newPassword)) {
      setError("Password must contain at least one digit or special character.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        email: trimmedEmail,
        otp: String(emailOtp.otp).trim(),
        new_password: newPassword,
        role: roleForBackend,
      });
      setDone(true);
    } catch (err) {
      setError(err.message || "Could not reset password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-vh-100 bg-body d-flex flex-column" style={{ fontFamily: fonts.body }}>
      <Navbar theme={theme} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />

      <div className="container-fluid flex-grow-1 px-0">
        <div className="row g-0 min-vh-100-minus-navbar">
          <div className="col-lg-6 col-xl-7 d-none d-lg-flex flex-column justify-content-center p-5 bg-body-tertiary border-end">
            <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-primary bg-opacity-10 text-primary border border-primary-subtle mb-4 small fw-bold">
              <KeyRound size={14} /> ACCOUNT RECOVERY
            </div>
            <h1 className="display-6 fw-bold text-body mb-3" style={{ fontFamily: fonts.display, letterSpacing: "-0.02em" }}>
              Reset your password with a one-time email code.
            </h1>
            <p className="lead fs-6 text-secondary mb-0" style={{ maxWidth: 520 }}>
              We send a 6-digit OTP to your registered email for every WIBIL role — Employee, Employer, Job Seeker, and Recruiter.
            </p>
          </div>

          <div className="col-12 col-lg-6 col-xl-5 d-flex flex-column justify-content-center p-4 p-md-5 bg-body">
            <div className="w-100 mx-auto" style={{ maxWidth: 480 }}>
              <div className="text-center mb-4">
                <div className="d-inline-flex p-3 rounded-4 bg-body-tertiary border mb-3 shadow-sm">
                  <Logo color="#0d6efd" size={32} subtitle={true} />
                </div>
                <h2 className="h5 fw-bold text-body mb-1" style={{ fontFamily: fonts.display }}>
                  Forgot Password
                </h2>
                <p className="small text-secondary mb-0">
                  Select your role, then verify the OTP sent to your email
                </p>
              </div>

              <div className="p-2 rounded-4 bg-body-tertiary border mb-3 shadow-sm">
                <div className="row g-1 text-center">
                  {[
                    { key: "employee", label: "Employee", icon: User },
                    { key: "employer", label: "Employer", icon: Building2 },
                    { key: "job_seeker", label: "Job Seeker", icon: Briefcase },
                    { key: "recruiter", label: "Recruiter", icon: Search },
                  ].map((b) => {
                    const isActive = activeTab === b.key;
                    const Icon = b.icon;
                    return (
                      <div key={b.key} className="col-6 col-sm-3">
                        <button
                          type="button"
                          onClick={() => switchTab(b.key)}
                          className={`w-100 py-3 px-1.5 rounded-3 btn border-0 d-flex flex-column align-items-center justify-content-center gap-1.5 ${isActive ? "btn-primary shadow" : "btn-link text-secondary text-decoration-none"}`}
                          style={{ fontFamily: fonts.display, fontSize: 13, fontWeight: 700 }}
                        >
                          <Icon size={18} />
                          <span style={{ whiteSpace: "nowrap" }}>{b.label}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {done ? (
                <div className="card shadow-sm border rounded-4 p-4 text-center">
                  <CheckCircle2 size={36} className="text-success mb-3" />
                  <h3 className="h5 fw-bold">Password updated</h3>
                  <p className="small text-secondary mb-4">You can now sign in with your new password.</p>
                  <Link to={loginPath} className="btn btn-primary fw-bold">
                    Back to Sign In
                  </Link>
                </div>
              ) : (
                <form onSubmit={resetPassword} className="card shadow-sm border rounded-4 overflow-hidden" autoComplete="off">
                  <div className="card-body p-4">
                    <ErrorBanner message={error} theme={theme} />

                    <div className="mb-3">
                      <label className="form-label small fw-bold text-uppercase text-secondary mb-1" style={{ letterSpacing: "0.05em" }}>
                        {emailConfig.label}
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-body-tertiary">
                          <Mail size={16} />
                        </span>
                        <input
                          type="email"
                          required
                          className="form-control"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); setError(""); }}
                          placeholder={emailConfig.placeholder}
                          autoComplete="off"
                          inputMode="email"
                          spellCheck={false}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary fw-bold btn-sm"
                          disabled={emailOtp.busy || !email.trim() || emailOtp.cooldown > 0}
                          onClick={emailOtp.send}
                        >
                          {otpButtonLabel(emailOtp, "Send OTP")}
                        </button>
                      </div>
                      <div className="form-text" style={{ fontSize: 12 }}>
                        Personal or company email is allowed. After sending, check inbox and spam/junk.
                      </div>
                    </div>

                    <EmailOtpStatus otpState={emailOtp} />

                    <div className="mb-3">
                      <label className="form-label small fw-bold text-uppercase text-secondary mb-1" style={{ letterSpacing: "0.05em" }}>
                        New Password
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-body-tertiary">
                          <Lock size={16} />
                        </span>
                        <PasswordInput
                          required
                          theme={theme}
                          className="form-control"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Min 8 characters"
                          autoComplete="new-password"
                          minLength={8}
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="form-label small fw-bold text-uppercase text-secondary mb-1" style={{ letterSpacing: "0.05em" }}>
                        Confirm New Password
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-body-tertiary">
                          <Lock size={16} />
                        </span>
                        <PasswordInput
                          required
                          theme={theme}
                          className="form-control"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Re-enter new password"
                          autoComplete="new-password"
                          minLength={8}
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={busy || emailOtp.busy}
                      className="btn btn-primary w-100 py-3 fw-bold text-uppercase d-flex align-items-center justify-content-center gap-2 shadow-sm"
                      style={{ fontFamily: fonts.display, letterSpacing: "0.03em" }}
                    >
                      <ShieldCheck size={16} /> {busy ? "Updating…" : "Reset Password"} <ChevronRight size={16} />
                    </button>
                  </div>
                </form>
              )}

              <div className="text-center mt-4">
                <Link to={loginPath} className="small fw-bold text-primary text-decoration-underline">
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
